import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { API, useAuth } from "../App";
import { ArrowLeft, Send, Mic, MicOff, Loader2, Sparkles, CalendarDays, ChevronDown, Crown } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";
import { useIsWebView } from "../hooks/useIsWebView";

const EMOTION_COLORS = {
  joy: "#10B981", happiness: "#10B981", alegría: "#10B981", felicidad: "#10B981", Freude: "#10B981", gioia: "#10B981",
  calm: "#3B82F6", calma: "#3B82F6", serenidad: "#3B82F6", Ruhe: "#3B82F6", calmo: "#3B82F6",
  sadness: "#6366F1", tristeza: "#6366F1", Traurigkeit: "#6366F1", tristezza: "#6366F1",
  anxiety: "#F59E0B", ansiedad: "#F59E0B", Angst: "#F59E0B", ansia: "#F59E0B",
  stress: "#EF4444", estrés: "#EF4444", Stress: "#EF4444",
  frustration: "#EC4899", frustración: "#EC4899", Frustration: "#EC4899", frustrazione: "#EC4899",
  loneliness: "#8B5CF6", soledad: "#8B5CF6", Einsamkeit: "#8B5CF6", solitudine: "#8B5CF6",
  excitement: "#F97316", entusiasmo: "#F97316", Begeisterung: "#F97316", eccitazione: "#F97316",
  gratitude: "#14B8A6", gratitud: "#14B8A6", Dankbarkeit: "#14B8A6", gratitudine: "#14B8A6",
  neutral: "#94A3B8", crisis: "#DC2626",
};

const getEmotionColor = (emotion) => {
  if (!emotion) return "#94A3B8";
  const lower = emotion.toLowerCase();
  for (const [key, color] of Object.entries(EMOTION_COLORS)) {
    if (lower.includes(key.toLowerCase())) return color;
  }
  return "#94A3B8";
};

const DiaryAI = () => {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const isWebView = useIsWebView();
  const plan = user?.plan || "descubre";
  const hasAccess = plan === "avanza";
  const [entries, setEntries] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [historyRes, summaryRes] = await Promise.all([
          axios.get(`${API}/diary/history?limit=30`, { withCredentials: true, timeout: 8000 }),
          axios.get(`${API}/diary/weekly-summary?lang=${lang}`, { withCredentials: true, timeout: 30000 }).catch(() => ({ data: null })),
        ]);
        setEntries(historyRes.data || []);
        if (summaryRes.data?.summary) setWeeklySummary(summaryRes.data);
      } catch (e) { console.error("Failed to load diary data:", e); }
      setLoadingHistory(false);
    };
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [entries]);

  const sendMessage = async (messageText) => {
    const finalText = (messageText || text).trim();
    if (!finalText || loading) return;
    setText("");
    setLoading(true);

    const tempEntry = { entry_id: "temp", user_text: finalText, ai_response: null, emotion: null, created_at: new Date().toISOString() };
    setEntries(prev => [...prev, tempEntry]);

    try {
      const res = await axios.post(`${API}/diary/message`, { text: finalText, lang }, { withCredentials: true, timeout: 30000 });
      setEntries(prev => prev.map(e => e.entry_id === "temp" ? res.data : e));
    } catch {
      setEntries(prev => prev.filter(e => e.entry_id !== "temp"));
      setText(finalText);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // iOS Safari doesn't support audio/webm - use mp4 fallback
      let mimeType = "audio/webm";
      let fileExt = "webm";
      if (!MediaRecorder.isTypeSupported("audio/webm")) {
        if (MediaRecorder.isTypeSupported("audio/mp4")) {
          mimeType = "audio/mp4";
          fileExt = "mp4";
        } else {
          // Last resort: let browser pick default
          mimeType = "";
          fileExt = "wav";
        }
      }
      const recorderOptions = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, recorderOptions);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, mimeType ? { type: mimeType } : {});
        if (blob.size < 1000) return;
        setTranscribing(true);
        const formData = new FormData();
        formData.append("audio", blob, `voice.${fileExt}`);
        formData.append("lang", lang);
        try {
          const res = await axios.post(`${API}/diary/voice`, formData, { withCredentials: true, timeout: 60000 });
          setEntries(prev => [...prev, res.data]);
        } catch (e) { console.error("Voice send failed:", e); }
        setTranscribing(false);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (e) { console.error("Microphone access failed:", e); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const today = new Date().toLocaleDateString(lang === "es" ? "es-ES" : lang === "de" ? "de-DE" : lang === "it" ? "it-IT" : "en-US", { weekday: "long", day: "numeric", month: "long" });

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col items-center justify-center" data-testid="diary-gate">
        <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />
        <div className="relative z-10 text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="font-['Outfit'] text-2xl font-semibold mb-3">{t("diary_gate_title")}</h1>
          <p className="text-white/50 mb-8">{t("diary_gate_desc")}</p>
          {!isWebView && (
            <Button onClick={() => navigate("/pricing")} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-black rounded-full px-8 py-3 font-medium" data-testid="diary-gate-upgrade-btn">
              <Crown className="w-4 h-4 mr-2" />{t("diary_gate_btn")}
            </Button>
          )}
          {isWebView && (
            <p className="text-white/40 text-sm" data-testid="webview-sub-text-diary-gate">{t("webview_sub_managed")}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col" data-testid="diary-ai-page">
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />

      <header className="relative z-20 border-b border-white/5 flex-shrink-0">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors" data-testid="diary-back-btn">
            <ArrowLeft className="w-5 h-5" /><span className="hidden sm:inline font-medium">{t("nav_back")}</span>
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="font-['Outfit'] font-semibold text-base sm:text-lg">{t("diary_title")}</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector variant="dark" />
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1"><FuturaIcon className="w-full h-full text-white" /></div>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex flex-col max-w-3xl mx-auto w-full px-4 sm:px-6">
        {/* Weekly summary toggle */}
        {weeklySummary?.summary && (
          <div className="mt-4 mb-2">
            <button onClick={() => setShowSummary(!showSummary)} className="flex items-center gap-2 text-xs text-white/40 hover:text-white/60 transition-colors" data-testid="weekly-summary-toggle">
              <CalendarDays className="w-3.5 h-3.5" />{t("diary_weekly_summary")}<ChevronDown className={`w-3 h-3 transition-transform ${showSummary ? "rotate-180" : ""}`} />
            </button>
            {showSummary && (
              <div className="mt-2 p-4 bg-white/5 border border-white/10 rounded-2xl animate-fade-in" data-testid="weekly-summary-content">
                <p className="text-sm text-white/70 leading-relaxed">{weeklySummary.summary}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {Object.entries(weeklySummary.emotions || {}).map(([em, count]) => (
                    <span key={em} className="text-[10px] px-2 py-1 rounded-full border" style={{ borderColor: `${getEmotionColor(em)}40`, color: getEmotionColor(em), backgroundColor: `${getEmotionColor(em)}10` }}>
                      {em} ({count})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Date */}
        <div className="text-center py-4 sm:py-6">
          <p className="text-xs text-white/30 capitalize">{today}</p>
        </div>

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto space-y-4 pb-4 min-h-0" data-testid="diary-entries">
          {loadingHistory ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 sm:py-20 animate-fade-in">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-[#002FA7]/20 to-[#5B21B6]/20 flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-[#002FA7]/60" />
              </div>
              <h2 className="font-['Outfit'] text-lg sm:text-xl font-semibold mb-2 text-white/80">{t("diary_empty_title")}</h2>
              <p className="text-white/40 text-sm max-w-sm mx-auto">{t("diary_empty_desc")}</p>
            </div>
          ) : (
            entries.map((entry) => (
              <div key={entry.entry_id} className="animate-fade-in">
                {/* User message */}
                <div className="flex justify-end mb-2">
                  <div className="max-w-[85%] sm:max-w-[75%] bg-gradient-to-br from-[#002FA7]/30 to-[#5B21B6]/30 border border-[#002FA7]/20 rounded-2xl rounded-br-md px-4 py-3">
                    <p className="text-sm text-white/90 whitespace-pre-wrap">{entry.user_text}</p>
                    {entry.transcription && <p className="text-[10px] text-white/30 mt-1 flex items-center gap-1"><Mic className="w-2.5 h-2.5" />{t("diary_voice_transcribed")}</p>}
                  </div>
                </div>
                {/* AI response */}
                {entry.ai_response ? (
                  <div className="flex justify-start mb-1">
                    <div className="max-w-[85%] sm:max-w-[75%] bg-white/5 border border-white/10 rounded-2xl rounded-bl-md px-4 py-3">
                      <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">{entry.ai_response}</p>
                      {entry.emotion && (
                        <span className="inline-block text-[10px] px-2 py-0.5 rounded-full mt-2 border" style={{ borderColor: `${getEmotionColor(entry.emotion)}40`, color: getEmotionColor(entry.emotion), backgroundColor: `${getEmotionColor(entry.emotion)}10` }}>
                          {entry.emotion}
                        </span>
                      )}
                      {entry.micro_challenge && !entry.is_crisis && (
                        <div className="mt-2 p-2.5 bg-amber-500/5 border border-amber-500/15 rounded-lg" data-testid="diary-challenge-box">
                          <p className="text-[10px] font-semibold text-amber-500/90 uppercase tracking-wider mb-1"><Sparkles className="w-3 h-3 inline mr-1" />{t("diary_challenge_label")}</p>
                          <p className="text-[11px] text-amber-400/80 leading-relaxed">{entry.micro_challenge}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-start mb-1">
                    <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-md px-4 py-3">
                      <Loader2 className="w-4 h-4 animate-spin text-white/40" />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input area */}
        <div className="flex-shrink-0 border-t border-white/5 py-3 sm:py-4 pb-14 sm:pb-4" data-testid="diary-input-area">
          {transcribing ? (
            <div className="flex items-center justify-center gap-2 py-3 text-white/50 text-sm"><Loader2 className="w-4 h-4 animate-spin" />{t("diary_transcribing")}</div>
          ) : recording ? (
            <div className="flex items-center justify-center gap-3">
              <div className="flex items-center gap-2 text-red-400 text-sm"><div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />{t("diary_recording")}</div>
              <Button onClick={stopRecording} className="bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 rounded-full px-6" data-testid="stop-recording-btn">
                <MicOff className="w-4 h-4 mr-2" />{t("diary_stop")}
              </Button>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <Textarea ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("diary_placeholder")} className="flex-1 min-h-[44px] max-h-[120px] bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#002FA7]/50 focus:ring-1 focus:ring-[#002FA7]/20 resize-none" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} disabled={loading} data-testid="diary-text-input" />
              <Button onClick={startRecording} variant="ghost" className="h-[44px] w-[44px] rounded-xl text-white/40 hover:text-white hover:bg-white/5 flex-shrink-0 p-0" data-testid="start-recording-btn">
                <Mic className="w-5 h-5" />
              </Button>
              <Button onClick={() => sendMessage()} disabled={loading || !text.trim()} className="h-[44px] bg-gradient-to-r from-[#002FA7] to-[#5B21B6] hover:opacity-90 text-white rounded-xl px-4 flex-shrink-0 disabled:opacity-30" data-testid="diary-send-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DiaryAI;
